/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import ModalInput from "../../ui/ModalTextInput";
import Dropdown from "../../ui/SelectDropdown";
import Button from "../../ui/Button";
import { RootState } from "../../../redux/app/store";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  fetchCategoriesAsync,
  fetchSizeChartsAsync,
  updateProductDetailsAsync,
} from "../../../redux/features/ProductSlice";
import MultiSelectDropdown from "../../ui/MultiSelectDropdown";

export interface SizeItem {
  label: string;
  quantity: number;
}

export interface EditProductFormData {
  name: string;
  description: string;
  price: string;
  quantity: number;
  colors: string[];
  sizeChartId: string;
  tags: string[];
  categoryIds: string; // single string now
}

interface EditProductModalProps {
  open: boolean;
  onClose: () => void;
  product: any;
  sizeCharts?: Array<{ label: string; value: string }>;
  onSave: () => void;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  open,
  onClose,
  product,
  onSave,
}) => {
  const { categories, sizeCharts, loading } = useAppSelector(
    (state: RootState) => state.products
  );
  const [formData, setFormData] = useState<EditProductFormData>({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || "",
    quantity: product?.quantity || 0,
    colors: product?.colors || [],
    sizeChartId: product?.sizeChart?.id || "",
    tags: product?.tags || [],
    categoryIds: product?.categoryIds?.id || "",
  });
  const [selectedVariants, setSelectedVariants] = useState(
    product?.sizes?.map((s: any) => ({
      label: s.sizeVariant.label,
      value: s.sizeVariant?.id,
      quantity: s.stock ?? 0,
      stockPrice: s.sizePrice,
    })) || []
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  // Get variants of selected size chart
  const variantsForSelectedSizeChart =
    sizeCharts?.items
      ?.find((sc: any) => sc.id === formData.sizeChartId)
      ?.variants?.map((v: any) => ({
        label: v.label,
        value: v.id,
      })) || [];

  // Combine all subcategories into one list
  const allSubcategories =
    categories
      ?.flatMap((cat: any) => cat.subcategories || [])
      ?.map((sub: any) => ({
        label: sub.title,
        value: sub.id,
      })) || [];
  // Combine all subcategories into one list
  const sizeChartByCategory =
    sizeCharts?.items?.map((sub: any) => ({
      label: sub.name,
      value: sub.id,
    })) || [];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const parsedValue = name === "quantity" ? Number(value) : value;
    setFormData({ ...formData, [name]: parsedValue });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSave = async () => {
    const updatedData = {
      ...formData,
      sizes: selectedVariants.map((v: any) => ({
        id: v.value,
        label: v.label,
        stock: v.quantity,
        sizePrice: v.sizePrice || 0,
      })),
    };

    try {
      await dispatch(
        updateProductDetailsAsync({ id: product?.id, updatedData })
      ).unwrap();
      onSave();
      onClose();
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  useEffect(() => {
    if (!categories?.length) {
      dispatch(fetchCategoriesAsync());
    }
  }, [dispatch]);

  useEffect(() => {
    // Determine parent category id from the selected subcategory
    const parentCategoryId = categories?.find((cat: any) =>
      cat.subcategories?.some((sub: any) => sub.id === formData.categoryIds)
    )?.id;

    if (parentCategoryId) {
      dispatch(
        fetchSizeChartsAsync({
          categoryId: parentCategoryId,
          page: 1,
          limit: 100,
        })
      );
    }
  }, [dispatch, formData.categoryIds]);

  console.log(product);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Product"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            label="Cancel"
            onClick={onClose}
            customStyle={{
              minWidth: "120px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Save"
            onClick={handleSave}
            customStyle={{ minWidth: "120px" }}
            isLoading={loading}
          />
        </div>
      }
    >
      <div className="p-6">
        <ModalInput
          label="Product Name"
          name="name"
          value={formData?.name}
          onChange={handleInputChange}
          required
          error={errors?.name}
        />

        <ModalInput
          label="Description"
          name="description"
          value={formData?.description}
          onChange={handleInputChange}
          required
          error={errors?.description}
        />

        {/* Subcategory Dropdown */}
        <Dropdown
          label="Category"
          name="categoryIds"
          options={allSubcategories}
          value={formData.categoryIds}
          onSelect={(value) =>
            setFormData({
              ...formData,
              categoryIds: value,
            })
          }
          placeholder="Select a subcategory"
        />

        <Dropdown
          label="Size Chart Template"
          name="sizeChartId"
          options={sizeChartByCategory}
          value={formData?.sizeChartId}
          onSelect={(value) => setFormData({ ...formData, sizeChartId: value })}
          error={errors?.sizeChartId}
          placeholder="Select size chart"
        />

        {/* Sizes */}
        {formData.sizeChartId && variantsForSelectedSizeChart.length > 0 && (
          <MultiSelectDropdown
            label="Variants"
            placeholder="Select variants"
            options={variantsForSelectedSizeChart}
            selectedValues={selectedVariants}
            onSelect={(vals) => {
              const updated = vals.map((v: any) => {
                const existing = selectedVariants.find(
                  (sv: any) => sv.value === v.value
                );
                return existing || { ...v, quantity: 0 };
              });
              setSelectedVariants(updated);
            }}
          />
        )}

        {selectedVariants.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium mb-2">
              Size Details
            </label>

            {selectedVariants.map((sv: any, index: any) => (
              <div key={sv.value} className="flex items-center gap-4 mb-3">
                <span className="w-20">{sv.label}</span>

                {/* Stock input */}
                <input
                  type="number"
                  min="0"
                  value={sv.quantity}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value) || 0;
                    const updated = [...selectedVariants];
                    updated[index].quantity = newValue;
                    setSelectedVariants(updated);
                  }}
                  className="w-24 border border-gray-300 rounded px-2 py-1"
                  placeholder="Stock"
                />

                {/* Size Price input */}
                <input
                  type="number"
                  min="0"
                  value={sv.sizePrice || 0}
                  onChange={(e) => {
                    const newPrice = parseInt(e.target.value) || 0;
                    const updated = [...selectedVariants];
                    updated[index].sizePrice = newPrice;
                    setSelectedVariants(updated);
                  }}
                  className="w-28 border border-gray-300 rounded px-2 py-1"
                  placeholder="Price"
                />
              </div>
            ))}
          </div>
        )}

        <ModalInput
          label="Colors (comma separated)"
          name="colors"
          value={formData.colors.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              colors: e.target.value.split(",").map((c) => c.trim()),
            })
          }
        />

        {/* Tags */}
        <ModalInput
          label="Tags (comma separated)"
          name="tags"
          value={formData?.tags?.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              tags: e.target.value.split(",").map((t) => t.trim()),
            })
          }
        />
      </div>
    </CustomModalContainer>
  );
};

export default EditProductModal;
